/**
 * Pure decision logic behind `useHandsOnControls`.
 *
 * Extracted for the same reason `SequenceMatcher` is: the React adapter runs
 * in `'use client'` and this repo's Jest environment is `node`, so anything
 * left inside the hook is untested. And there is real logic here — it is all
 * edges and windows, and every one of them fails silently:
 *
 *   - a pedal already down when the screen appears must not speak until it
 *     has come back up,
 *   - some pedals stream CC64 while held, so only the transition counts,
 *     never the stream,
 *   - a run ends **on** a press, so nothing counts for the first instants,
 *   - a screen answers once; the second gesture belongs to the next screen.
 *
 * The gate owns those four rules and nothing else. Subscribing to the piano,
 * knowing what `"advance"` means on a given screen, and calling anything, all
 * stay in the hook.
 */

export type HandsOnEvent =
  | { readonly kind: 'press'; readonly midi: number }
  | { readonly kind: 'release'; readonly midi: number }
  | { readonly kind: 'pedalDown' }
  | { readonly kind: 'pedalUp' };

/** What the screen should do about the gesture, or nothing. */
export type HandsOnAction = 'advance' | 'play' | null;

export type HandsOnGateOptions = {
  /** Clock reading at the moment the screen armed. */
  readonly armedAt: number;
  /** Nothing counts before this much has passed since `armedAt`. */
  readonly armMs: number;
  /** Whether the pedal was already down when the screen armed. */
  readonly pedalDown: boolean;
  /** Keys already down when the screen armed. */
  readonly held: Iterable<number>;
  /**
   * Notes that must be **held together** for a `'play'`, or `null` for any
   * single note. An empty list means the same as `null`: a screen that has no
   * opening group to ask for takes anything.
   */
  readonly playMidis?: readonly number[] | null;
  /** `false` on the screens where playing is exploration, not an answer. */
  readonly listensToPlay: boolean;
};

export class HandsOnGate {
  private readonly armedAt: number;
  private readonly armMs: number;
  private readonly expected: ReadonlySet<number> | null;
  private readonly listensToPlay: boolean;

  private pedalDown: boolean;
  private readonly held: Set<number>;
  private spent = false;

  constructor(options: HandsOnGateOptions) {
    this.armedAt = options.armedAt;
    this.armMs = options.armMs;
    this.pedalDown = options.pedalDown;
    this.held = new Set(options.held);
    this.listensToPlay = options.listensToPlay;

    const wanted = options.playMidis ?? null;

    this.expected = wanted === null || wanted.length === 0 ? null : new Set(wanted);
  }

  /**
   * Feed one gesture. `now` is the same clock `armedAt` was read from.
   *
   * Held keys are tracked whatever the answer, so a chord taken across the
   * arming window is still recognised once the window is open.
   */
  read(event: HandsOnEvent, now: number): HandsOnAction {
    if (event.kind === 'release') {
      this.held.delete(event.midi);

      return null;
    }

    if (event.kind === 'pedalUp') {
      this.pedalDown = false;

      return null;
    }

    if (event.kind === 'pedalDown') {
      // The stream a held pedal sends is not a second press.
      if (this.pedalDown) {
        return null;
      }

      this.pedalDown = true;

      return this.take('advance', now);
    }

    this.held.add(event.midi);

    if (!this.listensToPlay) {
      return null;
    }

    if (this.expected !== null && ![...this.expected].every((midi) => this.held.has(midi))) {
      return null;
    }

    return this.take('play', now);
  }

  private take(action: HandsOnAction, now: number): HandsOnAction {
    if (this.spent || now - this.armedAt < this.armMs) {
      return null;
    }

    this.spent = true;

    return action;
  }
}
